import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Trophy, Plus, Swords, Crown, Share2, Check, X } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  volume: "Total Volume (kg)",
  workouts: "Workouts Completed",
  reps: "Total Reps",
  bodyweight_loss: "Body Weight Lost (kg)",
};

const formatScore = (type, val) => {
  if (val == null) return "0";
  if (type === "workouts" || type === "reps") return Math.round(val).toString();
  return Number(val).toFixed(1);
};

export default function Challenges() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [buddies, setBuddies] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    opponent_id: "",
    challenge_type: "volume",
    duration_days: 7,
    exercise_id: "",
    title: "",
  });

  const load = async () => {
    const [c, b, e] = await Promise.all([api.get("/challenges"), api.get("/buddies"), api.get("/exercises")]);
    setItems(c.data);
    setBuddies(b.data.accepted || []);
    setExercises(e.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.opponent_id) { toast.error("Pick a buddy"); return; }
    const exObj = exercises.find((x) => x.exercise_id === form.exercise_id);
    try {
      await api.post("/challenges", {
        opponent_id: form.opponent_id,
        challenge_type: form.challenge_type,
        duration_days: Number(form.duration_days),
        exercise_id: form.challenge_type === "reps" ? form.exercise_id : null,
        exercise_name: form.challenge_type === "reps" ? exObj?.name : null,
        title: form.title,
      });
      toast.success("Challenge sent!");
      setOpen(false);
      setForm({ opponent_id: "", challenge_type: "volume", duration_days: 7, exercise_id: "", title: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const accept = async (cid) => { await api.post(`/challenges/${cid}/accept`); toast.success("Accepted"); load(); };
  const decline = async (cid) => { await api.post(`/challenges/${cid}/decline`); load(); };

  const shareWin = async (ch) => {
    const winner = ch.winner_id === ch.challenger_id ? ch.challenger_name : ch.opponent_name;
    const loser = ch.winner_id === ch.challenger_id ? ch.opponent_name : ch.challenger_name;
    const text = ch.winner_id
      ? `🏆 ${winner} won the "${ch.title}" challenge against ${loser} on GYM with GRIZZ!`
      : `🤝 ${ch.challenger_name} & ${ch.opponent_name} tied on the "${ch.title}" challenge.`;
    const url = window.location.origin;
    if (navigator.share) navigator.share({ title: "Challenge Result", text, url }).catch(() => {});
    else { navigator.clipboard.writeText(text + " " + url); toast.success("Copied — paste to Facebook/Instagram"); }
    // Auto post to feed
    await api.post("/feed", { text, kind: "challenge", meta: { challenge_id: ch.challenge_id } });
    toast.success("Shared to your feed");
  };

  const pending = items.filter((c) => c.status === "pending");
  const active = items.filter((c) => c.status === "active");
  const past = items.filter((c) => ["completed", "declined"].includes(c.status));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl uppercase">Challenges</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-challenge-btn" disabled={buddies.length === 0} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
              <Swords className="w-3 h-3 mr-1" /> New
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
            <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Challenge a Buddy</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Opponent</Label>
                <Select value={form.opponent_id} onValueChange={(v) => setForm({...form, opponent_id: v})}>
                  <SelectTrigger data-testid="ch-opponent-select" className="mt-2 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue placeholder="Pick a buddy" /></SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-800">
                    {buddies.map((b) => {
                      const otherId = b.from_user_id === user.user_id ? b.to_user_id : b.from_user_id;
                      const otherName = b.from_user_id === user.user_id ? b.to_name : b.from_name;
                      return <SelectItem key={otherId} value={otherId}>{otherName}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Type</Label>
                <Select value={form.challenge_type} onValueChange={(v) => setForm({...form, challenge_type: v})}>
                  <SelectTrigger data-testid="ch-type-select" className="mt-2 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-800">
                    {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.challenge_type === "reps" && (
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Exercise</Label>
                  <Select value={form.exercise_id} onValueChange={(v) => setForm({...form, exercise_id: v})}>
                    <SelectTrigger data-testid="ch-exercise-select" className="mt-2 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue placeholder="Pick an exercise" /></SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800 max-h-60">
                      {exercises.map((e) => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Duration (days)</Label>
                <Input data-testid="ch-days-input" type="number" min={1} max={90} value={form.duration_days} onChange={(e) => setForm({...form, duration_days: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Title (optional)</Label>
                <Input data-testid="ch-title-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Squat-Off March" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <Button data-testid="ch-submit-btn" type="submit" className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">Send challenge</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {buddies.length === 0 && <p className="text-sm text-neutral-500 mb-4">Add a buddy first to start a challenge.</p>}

      {pending.length > 0 && (
        <Section title="Pending" items={pending}>
          {pending.map((ch) => (
            <ChallengeCard key={ch.challenge_id} ch={ch} userId={user.user_id} onAccept={accept} onDecline={decline} onShare={shareWin} />
          ))}
        </Section>
      )}
      {active.length > 0 && (
        <Section title="Live" items={active}>
          {active.map((ch) => (
            <ChallengeCard key={ch.challenge_id} ch={ch} userId={user.user_id} onAccept={accept} onDecline={decline} onShare={shareWin} />
          ))}
        </Section>
      )}
      {past.length > 0 && (
        <Section title="History" items={past}>
          {past.map((ch) => (
            <ChallengeCard key={ch.challenge_id} ch={ch} userId={user.user_id} onAccept={accept} onDecline={decline} onShare={shareWin} />
          ))}
        </Section>
      )}
      {items.length === 0 && (
        <div className="border border-dashed border-neutral-800 p-8 text-center">
          <Swords className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
          <div className="text-sm text-neutral-400">No challenges yet. Start one with a buddy.</div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <h3 className="font-display text-lg uppercase mb-2 text-neutral-400">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ChallengeCard({ ch, userId, onAccept, onDecline, onShare }) {
  const isPending = ch.status === "pending";
  const isCompleted = ch.status === "completed";
  const isOpponent = ch.opponent_id === userId;
  const myScore = ch.challenger_id === userId ? ch.challenger_score : ch.opponent_score;
  const oppScore = ch.challenger_id === userId ? ch.opponent_score : ch.challenger_score;
  const oppName = ch.challenger_id === userId ? ch.opponent_name : ch.challenger_name;
  const total = (myScore || 0) + (oppScore || 0);
  const myPct = total > 0 ? (myScore / total) * 100 : 50;
  const won = isCompleted && ch.winner_id === userId;
  const lost = isCompleted && ch.winner_id && ch.winner_id !== userId;
  const tied = isCompleted && !ch.winner_id;
  const endsAt = new Date(ch.end_date);

  return (
    <div data-testid={`ch-card-${ch.challenge_id}`} className={`border ${won ? "border-blue-500" : "border-neutral-800"} p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-display text-xl uppercase">{ch.title}</div>
          <div className="text-xs text-neutral-500 uppercase tracking-wider">vs {oppName} · {TYPE_LABEL[ch.challenge_type]} · {ch.duration_days}d</div>
        </div>
        {won && <Crown className="w-6 h-6 text-blue-500" />}
        {lost && <Trophy className="w-6 h-6 text-neutral-600" />}
      </div>

      {!isPending && (
        <>
          <div className="flex items-center justify-between text-xs uppercase tracking-wider text-neutral-400 mb-1">
            <span>You · {formatScore(ch.challenge_type, myScore)}</span>
            <span>{formatScore(ch.challenge_type, oppScore)} · {oppName}</span>
          </div>
          <div className="h-2 bg-neutral-800 flex">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${myPct}%` }} />
            <div className="h-full bg-neutral-600 transition-all" style={{ width: `${100 - myPct}%` }} />
          </div>
          {!isCompleted && (
            <div className="text-xs text-neutral-500 mt-2">Ends {endsAt.toLocaleDateString()}</div>
          )}
        </>
      )}

      <div className="flex gap-2 mt-3">
        {isPending && isOpponent && (
          <>
            <Button data-testid={`accept-ch-${ch.challenge_id}`} size="sm" onClick={() => onAccept(ch.challenge_id)} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8">
              <Check className="w-3 h-3 mr-1" /> Accept
            </Button>
            <Button data-testid={`decline-ch-${ch.challenge_id}`} size="sm" variant="outline" onClick={() => onDecline(ch.challenge_id)} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider h-8">
              <X className="w-3 h-3 mr-1" /> Decline
            </Button>
          </>
        )}
        {isPending && !isOpponent && <span className="text-xs text-neutral-500 uppercase tracking-wider">Waiting on {oppName}</span>}
        {isCompleted && (
          <>
            <span className={`text-xs uppercase tracking-widest font-bold ${won ? "text-blue-500" : tied ? "text-neutral-300" : "text-neutral-500"}`}>
              {won ? "🏆 BRAGGING RIGHTS" : tied ? "🤝 TIE" : "Better luck next time"}
            </span>
            <Button data-testid={`share-ch-${ch.challenge_id}`} size="sm" variant="ghost" onClick={() => onShare(ch)} className="ml-auto text-blue-500 hover:text-blue-400 uppercase tracking-wider font-display h-8">
              <Share2 className="w-3 h-3 mr-1" /> Share
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
